import { toast as baseToast } from "@/shared/hooks/use-toast"
import { CheckCircle, XCircle, AlertCircle, Info } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import { ToastAction } from "@/shared/ui/toast"

export type ToastType = "success" | "error" | "warning" | "info"

export interface ToastConfig {
  /** Duração em milissegundos (usa padrão do tipo se não especificado) */
  duration?: number
  /** Se deve mostrar o botão de fechar (padrão: true) */
  showCloseButton?: boolean
  /** Ação personalizada (botão) */
  action?: {
    label: string
    onClick: () => void
  }
  /** Classe CSS adicional */
  className?: string
  /** Contexto para aplicar configurações específicas */
  context?: ToastContext
}

export interface ToastNotificationProps {
  /** Tipo do toast */
  type: ToastType
  /** Título da mensagem */
  title: string
  /** Descrição da mensagem (opcional) */
  description?: string
  /** Configurações adicionais */
  config?: ToastConfig
}

// Configurações globais para o sistema de toast
const TOAST_CONFIG = {
  // Durações padrão para cada tipo (em milissegundos)
  DURATIONS: {
    success: 5000,
    error: 7000,
    warning: 6000,
    info: 5000,
  } as Record<ToastType, number>,

  // Se deve mostrar o botão de fechar por padrão
  SHOW_CLOSE_BUTTON: true,
} as const

// Configurações específicas para diferentes contextos
const CONTEXT_TOAST_CONFIG = {
  // Para operações de formulário
  form: {
    success: { duration: 4000 },
    error: { duration: 8000 },
  },
  
  // Para operações de API
  api: {
    success: { duration: 3000 },
    error: { duration: 10000 },
  },
  
  // Para notificações de sistema
  system: {
    info: { duration: 6000 },
    warning: { duration: 8000 },
  },
} as const

export type ToastContext = keyof typeof CONTEXT_TOAST_CONFIG

// Configurações padrão para cada tipo
const getToastDefaults = (type: ToastType, context?: ToastContext): Partial<ToastConfig> => {
  const baseDefaults = {
    duration: TOAST_CONFIG.DURATIONS[type],
    showCloseButton: TOAST_CONFIG.SHOW_CLOSE_BUTTON,
  }

  // Aplica configurações específicas do contexto se fornecido
  if (context && CONTEXT_TOAST_CONFIG[context] && CONTEXT_TOAST_CONFIG[context][type]) {
    return {
      ...baseDefaults,
      ...CONTEXT_TOAST_CONFIG[context][type],
    }
  }

  return baseDefaults
}

// Ícones para cada tipo
const toastIcons: Record<ToastType, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
}

// Variantes de estilo para cada tipo
const toastVariants: Record<ToastType, string> = {
  success: "border-green-200 bg-green-50 text-green-800",
  error: "border-red-200 bg-red-50 text-red-800",
  warning: "border-yellow-200 bg-yellow-50 text-yellow-800",
  info: "border-blue-200 bg-blue-50 text-blue-800",
}

/**
 * Componente simplificado para exibir notificações toast
 * 
 * @example
 * ```tsx
 * // Toast de sucesso simples
 * showToast({
 *   type: "success",
 *   title: "Operação realizada com sucesso!",
 *   description: "Os dados foram salvos corretamente."
 * })
 * 
 * // Toast de erro com ação personalizada
 * showToast({
 *   type: "error",
 *   title: "Erro ao salvar",
 *   description: "Tente novamente em alguns instantes.",
 *   config: {
 *     action: {
 *       label: "Tentar novamente",
 *       onClick: () => retrySave()
 *     }
 *   }
 * })
 * ```
 */
export function showToast({ type, title, description, config = {} }: ToastNotificationProps) {
  const defaults = getToastDefaults(type, config.context)
  const finalConfig = { ...defaults, ...config }
  
  const variantClass = toastVariants[type]

  return baseToast({
    title: title,
    description: description,
    variant: type === "error" ? "destructive" : "default",
    className: cn(
      variantClass,
      finalConfig.className
    ),
    duration: finalConfig.duration,
    action: finalConfig.action ? (
      <ToastAction altText={finalConfig.action.label} onClick={finalConfig.action.onClick}>
        {finalConfig.action.label}
      </ToastAction>
    ) : undefined,
  })
}

// Funções de conveniência para cada tipo
export const toast = {
  success: (title: string, description?: string, config?: ToastConfig) =>
    showToast({ type: "success", title, description, config }),
  
  error: (title: string, description?: string, config?: ToastConfig) =>
    showToast({ type: "error", title, description, config }),
  
  warning: (title: string, description?: string, config?: ToastConfig) =>
    showToast({ type: "warning", title, description, config }),
  
  info: (title: string, description?: string, config?: ToastConfig) =>
    showToast({ type: "info", title, description, config }),

  // Funções com contexto pré-definido
  form: {
    success: (title: string, description?: string, config?: Omit<ToastConfig, 'context'>) =>
      showToast({ type: "success", title, description, config: { ...config, context: "form" } }),
    
    error: (title: string, description?: string, config?: Omit<ToastConfig, 'context'>) =>
      showToast({ type: "error", title, description, config: { ...config, context: "form" } }),
  },

  api: {
    success: (title: string, description?: string, config?: Omit<ToastConfig, 'context'>) =>
      showToast({ type: "success", title, description, config: { ...config, context: "api" } }),
    
    error: (title: string, description?: string, config?: Omit<ToastConfig, 'context'>) =>
      showToast({ type: "error", title, description, config: { ...config, context: "api" } }),
  },

  system: {
    info: (title: string, description?: string, config?: Omit<ToastConfig, 'context'>) =>
      showToast({ type: "info", title, description, config: { ...config, context: "system" } }),
    
    warning: (title: string, description?: string, config?: Omit<ToastConfig, 'context'>) =>
      showToast({ type: "warning", title, description, config: { ...config, context: "system" } }),
  },
}

export default toast
